import React, { useEffect, useState } from 'react';
import * as ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import moment from 'moment/moment';
import { Button, Icons, Row } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import {
  EXPIRATION_DATE_69_FEDERAL_NUMBER, FEDERAL_LAW_NUMBERS, USER_TYPE, USER_TYPE_LIST,
} from '../../../configs';
import FieldDTOWithCol from '../../../components/fields/Abstract/FieldDTOWithCol';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import { createVehicles, editVehiclesById, loadVehicleFieldsValidation } from '../actions';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import { getResponseError, responseLogWithNotification, setFormError } from '../../../utils/utils';
import VehiclesDTO from '../../../dto/VehiclesDTO';
import DatePicker from '../../../components/fields/DatePicker/DatePicker';
import ApplicantForm from '../../Application/components/FormBlocks/ApplicantForm';
import InsuranceForm from '../../Application/components/FormBlocks/InsuranceForm';
import CarFormRegistry from './CarFormRegistry';
import { showGlobalConfirm } from '../../System/actions';
import { infoFormatterIntl, renderRegistryValidationMessage } from '../../../utils/formatters';

export default function VehicleForm(props) {
  const {
    onClose = () => {},
    loadData = () => {},
    data = {},
    isEdit = false,
    formMeta,
  } = props;
  const dispatch = useDispatch();
  const { federal_law_number = FEDERAL_LAW_NUMBERS.FEDERAL_LAW_580 } = data;
  const [federalLawNumber, setFederalLawNumber] = useState(federal_law_number);
  const [applicantType, setApplicantType] = useState(data && data.legal_entity_type ? data.legal_entity_type : USER_TYPE.LEGAL);
  const {
    watch,
    control,
    getValues,
    setValue,
    formState: { isDirty, dirtyFields, errors },
    setError,
    handleSubmit,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      legal_entity_type: applicantType,
      ...data,
    },
  });

  React.useEffect(() => {
    const subscription = watch(values => {
      setApplicantType(values.legal_entity_type);
      if (values && values.effective_date && typeof values.effective_date !== 'undefined') {
        const targetDate = moment(EXPIRATION_DATE_69_FEDERAL_NUMBER, 'DD.MM.YYYY');
        const currentDate = moment(getValues('effective_date'));
        setFederalLawNumber(currentDate.isAfter(targetDate) || currentDate.isSame(targetDate) ? FEDERAL_LAW_NUMBERS.FEDERAL_LAW_580 : FEDERAL_LAW_NUMBERS.FEDERAL_LAW_69);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (federal_law_number) {
      setValue('federal_law_number', federalLawNumber);
    }
  }, [federalLawNumber]);

  const handleValidateAndSave = async () => {
    if (!data?.id) {
      return handleSave();
    }
    const values = getValues();
    dispatch(loadVehicleFieldsValidation({ id: data.id, data: { input_data: values } })).then(data => {
      const checkErrors = data?.checks || {};
      const errors = data?.errors || {};
      const checkErrorsKeys = Object.keys(checkErrors);
      const errorsKeys = Object.keys(errors);
      if (Object.values(checkErrors).filter(f => f.status !== 2).length + errorsKeys.length) {
        dispatch(showGlobalConfirm({
          confirmTitle: 'Реестровая запись содержит ошибки в полях',
          confirmMessage: renderRegistryValidationMessage(errors, checkErrors, checkErrorsKeys, errorsKeys),
          confirmButtonText: 'Сохранить',
          confirmClassName: 'remove-preset',
        })).then(() => {
          handleSave();
        }).catch(() => {});
      } else {
        handleSave();
      }
    }).catch(responseLogWithNotification);
  };

  const handleSave = async () => {
    const keys = Object.keys(errors);
    if (errors && keys.length) {
      errorNotification('Сохранение формы ЛТ', 'Ошибка заполнения формы');
      const { ref } = errors[keys[0]];
      ref.focus();
      setError('number', { type: 'custom', message: 'custom message' });
      return null;
    }
    const action = isEdit ? editVehiclesById : createVehicles;
    const values = getValues();
    dispatch(action(values))
      .then(() => {
        loadData();
        onClose();
        successNotification('Сохранение формы ЛТ', 'Сформировано Решение о внесении изменений');
      })
      .catch(response => {
        setFormError(response, setError);
        errorNotification('Сохранение формы ЛТ', getResponseError({ error: response && response.message ? response.message : '' }));
      });
  };

  const DTO = new VehiclesDTO();
  const attributes = DTO.getAttributesByName();

  return ReactDOM.createPortal(
    <div className="application-form">
      <div className="application-form-header">
        <div className="breadcrumbs">
          <span className="breadcrumbs-prev">Реестры</span>
          <Icons width={22} height={22} customIcon icon="arrow-right" className="breadcrumbs-arrow" />
          <span className="breadcrumbs-prev">Реестр ЛТ</span>
          <Icons width={22} height={22} customIcon icon="arrow-right" className="breadcrumbs-arrow" />
          <span className="breadcrumbs-prev"> Запись ЛТ</span>
          <Icons width={22} height={22} customIcon icon="arrow-right" className="breadcrumbs-arrow" />
          <span
            className="breadcrumbs-current"
          >Запись ЛТ № {data?.number || 'Без номера'} ({isEdit ? 'редактирование' : 'создание'})
          </span>
        </div>
        <div className="close-button" onClick={onClose}>
          <Icons width={24} height={24} customIcon icon="close" />
        </div>
      </div>
      <div className="application-form-body">
        <form className="application-form-content small" onSubmit={handleSubmit(handleValidateAndSave)}>
          <div className="application-form-fields-header">Заполните информацию о документе</div>
          <div className="application-form-fields">
            <div className="application-form-fields-wrapper custom-scroll-theme">
              <Row className="row-cols-3">
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={TextInputWithLabel}
                  dtoObject={attributes.number}
                  variant="material"
                  disabled
                />
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={DatePicker}
                  dtoObject={attributes.effective_date}
                  withLabel
                  changeFormat="YYYY-MM-DD"
                  variant="material"
                />
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={DatePicker}
                  changeFormat="YYYY-MM-DD"
                  dtoObject={attributes.expiration_date}
                  withLabel
                  variant="material"
                />
              </Row>
              <div className="f4 mt-3">Водительское удостоверение</div>
              <ApplicantForm
                watch={watch}
                control={control}
                setValue={setValue}
                formMeta={formMeta}
                getValues={getValues}
                applicantType={applicantType}
                federal_law_number={federalLawNumber}
                availableTypes={USER_TYPE_LIST.filter(f => f.value !== USER_TYPE.PRIVATE_PERSON_OLD)}
                hideFileInput
              />
              <div className="f4 mt-3">Информация по ОСАГО владельца ТС</div>
              <InsuranceForm
                control={control}
                formMeta={formMeta}
              />
              <div className="f4 mt-3">Информация о транспортном средстве</div>
              <CarFormRegistry
                control={control}
                formMeta={formMeta}
                federal_law_number={federalLawNumber}
                watch={watch}
                getValues={getValues}
              />
              <Row className="row-cols-2">
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={TextInputWithLabel}
                  dtoObject={attributes.decision}
                  variant="material"
                />
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={DatePicker}
                  changeFormat="YYYY-MM-DD"
                  dtoObject={attributes.decision_date}
                  variant="material"
                  withLabel
                />
              </Row>
            </div>
          </div>
          <div className="application-form-footer">
            <Button variant="secondary" onClick={() => onClose()}>Отменить</Button>
            <Button type="submit">{isEdit ? 'Сохранить' : 'Создать'}</Button>
          </div>
        </form>
      </div>
    </div>,
    document.getElementById('app-content'),
  );
}
